import { useCallback, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { usePageLoadTracking } from '@/hooks/pageLoadTracking/usePageLoadTracking'

import { debounce } from '@/utils/functionalUtils'
import { resolveValue } from '@/utils/stringUtils'

import { PageTitle } from '@/components/pageTitle'
import { FooterPolicy } from '@/components/policyLinkBlock'
import { TextHTML } from '@/components/typography'

import { useAuth } from '@/auth/useAuth'

import { CustomPageProps } from './types'

export const CustomPage = ({
  pageName,
  pageConfig,
  setLastVisitedItem,
  config,
}: CustomPageProps) => {
  const { user } = useAuth()
  const navigate = useNavigate()

  const {
    nextUrl,
    title,
    tabTitle,
    pageDescription,
    PageContent,
    texts,
    images,
    headerBlock,
    blocks,
    reviews,
    reviewsTitle,
    policyBlock,
    buttonName,
    speed,
    stepsText,
    customReadyTexts,
    imageCaption,
    feedback,
    popupConfig,
    lottieOptions,
  } = pageConfig

  const pageTitle = useMemo(() => {
    return !title ? title : resolveValue<string>(title)
  }, [title])

  usePageLoadTracking({
    pageName,
    config,
    user,
    title: pageTitle || tabTitle || '',
  })

  const goToNextPage = useCallback(async () => {
    await debounce(() => {
      navigate(nextUrl)
      setLastVisitedItem(pageName)
    }, 300)()
  }, [navigate, nextUrl, pageName, setLastVisitedItem])

  const className = `page page-survey page-${pageName}`
  return (
    <div className={className}>
      {pageTitle && <PageTitle title={pageTitle} />}
      {pageDescription && <TextHTML className="page-description" title={pageDescription} />}

      <PageContent
        goToNextPage={goToNextPage}
        texts={texts}
        images={images}
        headerBlock={headerBlock}
        blocks={blocks}
        reviewsTitle={reviewsTitle}
        reviews={reviews}
        buttonName={buttonName}
        speed={speed}
        stepsText={stepsText}
        customReadyTexts={customReadyTexts}
        imageCaption={imageCaption}
        feedback={feedback}
        popupConfig={popupConfig}
        lottieOptions={lottieOptions}
      />
      {policyBlock && <FooterPolicy />}
    </div>
  )
}
